// =============================================================================
// features/admin/inbox-assignments/api.ts — HTTP client para a tela de
// atribuição usuário↔canal (F47-S08).
//
// Endpoints consumidos:
//   GET    /api/livechat/inbox-assignments      — lista as atribuições da organização
//   POST   /api/livechat/inbox-assignments      — cria (idempotente por par usuário/canal)
//   DELETE /api/livechat/inbox-assignments/:id  — remove
//
// RBAC: livechat:inbox_assignment:manage (backend valida via authenticate()
// + authorize() em inbox-assignments.routes.ts; API retorna 403 sem
// permissão). Deliberadamente SEM featureGate de livechat.inbox_scope.enabled
// — o CRUD precisa funcionar mesmo com a flag desligada, para popular
// atribuições antes do flip (ver header de inbox-assignments.routes.ts).
// Único ponto de acesso à rede — nunca useEffect+fetch.
// =============================================================================
import { z } from 'zod';

import { api } from '../../../lib/api';

// ---------------------------------------------------------------------------
// Zod schemas — validação runtime das respostas
// ---------------------------------------------------------------------------

const ChannelAssignmentResponseSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  userFullName: z.string(),
  userEmail: z.string(),
  channelId: z.string().uuid(),
  channelName: z.string(),
  channelProvider: z.string(),
  createdAt: z.string(),
  createdByUserId: z.string().uuid().nullable(),
});

const ListAssignmentsResponseSchema = z.object({
  data: z.array(ChannelAssignmentResponseSchema),
});

export type ChannelAssignmentResponse = z.infer<typeof ChannelAssignmentResponseSchema>;

export interface CreateAssignmentBody {
  userId: string;
  channelId: string;
}

// ---------------------------------------------------------------------------
// Funções de acesso à API
// ---------------------------------------------------------------------------

/** GET /api/livechat/inbox-assignments — lista as atribuições da organização. */
export async function fetchInboxAssignments(): Promise<ChannelAssignmentResponse[]> {
  const json = await api.get<unknown>('/api/livechat/inbox-assignments');
  return ListAssignmentsResponseSchema.parse(json).data;
}

/** POST /api/livechat/inbox-assignments — cria a atribuição usuário↔canal. */
export async function createInboxAssignment(
  body: CreateAssignmentBody,
): Promise<ChannelAssignmentResponse> {
  const json = await api.post<unknown>('/api/livechat/inbox-assignments', body);
  return ChannelAssignmentResponseSchema.parse(json);
}

/** DELETE /api/livechat/inbox-assignments/:id — remove a atribuição. */
export async function deleteInboxAssignment(id: string): Promise<void> {
  await api.delete(`/api/livechat/inbox-assignments/${encodeURIComponent(id)}`);
}
